import React, { CSSProperties, FC, LegacyRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import SwiperType from 'swiper';
import { useSwipeNavigation } from './hooks/useSwipeNavigation';

export type SliderNavigationProps = {
  compactNavigation?: boolean;
  arrowIconNavigation?: boolean;
  arrows?: boolean;
  innerArrows?: boolean;
  solidArrows?: boolean;
  prevButtonStyles?: CSSProperties;
  nextButtonStyles?: CSSProperties;
  prevArrowStyles?: CSSProperties;
  nextArrowStyles?: CSSProperties;
  totalSlides?: number;
  swiperRef?: SwiperType;
  navigationPrevRef?: LegacyRef<HTMLDivElement>;
  navigationNextRef?: LegacyRef<HTMLDivElement>;
};

const ARROW_STYLES = {
  compact: {
    className: 'h-20 w-20 hover:cursor-pointer',
    strokeWidth: 1,
    color: 'var(--color-neutral-black-400-v)',
  },
  icon: {
    className: 'h-24 w-24 hover:cursor-pointer',
    strokeWidth: 1,
    color: 'var(--color-neutral-black-v)',
  },
} as const;

const SliderNavigation: FC<SliderNavigationProps> = ({
  compactNavigation,
  arrowIconNavigation,
  arrows,
  innerArrows,
  solidArrows,
  prevButtonStyles,
  nextButtonStyles,
  prevArrowStyles,
  nextArrowStyles,
  totalSlides,
  swiperRef,
}) => {
  const { showPrevArrow, showNextArrow } = useSwipeNavigation({ swiperRef, totalSlides });

  if (compactNavigation) {
    return (
      <div className="mt-10 justify-center gap-16" style={{ display: arrows ? 'flex' : 'none' }}>
        <ChevronLeftIcon {...ARROW_STYLES.compact} onClick={() => swiperRef?.slidePrev()} />
        <div className="flex font-body text-14 font-regular leading-loose text-secondary-black">
          <span>{((swiperRef?.activeIndex ?? 0) % (totalSlides ?? 1)) + 1}</span>
          <span>/</span>
          <span>{totalSlides}</span>
        </div>
        <ChevronRightIcon {...ARROW_STYLES.compact} onClick={() => swiperRef?.slideNext()} />
      </div>
    );
  }

  const arrowClassNames = {
    base: 'slider_arrow',
    prev: 'slider_arrow_prev',
    next: 'slider_arrow_next',
    inner: innerArrows ? 'slider_arrow_inner' : '',
    solid: solidArrows ? 'slider_arrow_solid' : '',
  };

  const arrowStyles = {
    position: 'absolute',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as const;

  const iconStyles = {
    transform: 'translateY(-20px)',
  };

  if (!arrows) return null;

  const handlePrevClick = () => {
    swiperRef?.slidePrev();
  };

  const handleNextClick = () => {
    swiperRef?.slideNext();
  };

  return (
    <div>
      {showPrevArrow && (
        <div
          className={`${arrowClassNames.base} ${arrowClassNames.prev} ${arrowClassNames.inner} ${arrowClassNames.solid}`}
          style={{ ...prevButtonStyles, ...arrowStyles }}
          onClick={handlePrevClick}
        >
          {arrowIconNavigation ? (
            <ArrowLeftIcon {...ARROW_STYLES.icon} style={iconStyles} onClick={() => swiperRef?.slidePrev()} />
          ) : (
            <span className="arrow" style={prevArrowStyles} />
          )}
        </div>
      )}

      {showNextArrow && (
        <div
          className={`${arrowClassNames.base} ${arrowClassNames.next} ${arrowClassNames.inner} ${arrowClassNames.solid}`}
          style={{ ...nextButtonStyles, ...arrowStyles }}
          onClick={handleNextClick}
        >
          {arrowIconNavigation ? (
            <ArrowRightIcon {...ARROW_STYLES.icon} style={iconStyles} onClick={() => swiperRef?.slideNext()} />
          ) : (
            <span className="arrow" style={nextArrowStyles} />
          )}
        </div>
      )}
    </div>
  );
};

export default SliderNavigation;
